import NextLink from "next/link";
import { Box } from "@chakra-ui/react";
import { ColorIcon, customEvent, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";
import { ParlorDetailProps } from "@/types/parlor";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";
import { isHiddenParlor } from "@/utils/parlorPriceDisclosure";

type IntentTrigger = "intent-call" | "intent-cost" | "intent-visit";

const track = (trigger: IntentTrigger, parlorId: string) =>
  customEvent({
    newGtm: {
      path: "parlor_detail",
      trigger_and_target: trigger,
      properties: { parlor_id: parlorId },
    },
  });

/**
 * 바로가기 한 줄. 왼쪽 의미 아이콘, 가운데 문구, 오른쪽 화살표 아이콘.
 * emphasis 는 임종접수 줄 하나에만 쓴다. 장례가 난 사람이 가장 먼저 찾아야 하는 줄이라서다.
 */
function ShortcutRow({
  icon,
  title,
  desc,
  emphasis = false,
}: {
  icon: "icon-call-mono" | "icon-coin-mono" | "icon-pin-location-mono";
  title: string;
  desc?: string;
  emphasis?: boolean;
}) {
  return (
    <BaseStack
      direction="horizontal"
      alignItems="center"
      spacing={12}
      cursor="pointer"
      padding="14px 16px"
      bg={emphasis ? baseColors.orange_50 : baseColors.white}
      border={`1px solid ${emphasis ? baseColors.orange_200 : baseColors.line_normal}`}
      borderRadius="10px"
    >
      <BaseStack
        alignItems="center"
        justifyContent="center"
        w={36}
        h={36}
        flexShrink={0}
        borderRadius="50%"
        bg={emphasis ? baseColors.orange_100 : baseColors.fill_normal}
      >
        <ColorIcon
          name={icon}
          color={emphasis ? baseColors.orange_500 : baseColors.label_alternative_1}
          size={18}
        />
      </BaseStack>
      <Box flex={1} minWidth={0}>
        <Box
          className="body_2_normal_sb"
          color={emphasis ? baseColors.orange_600 : baseColors.label_normal}
        >
          {title}
        </Box>
        {desc && (
          <>
            <Spacing gap={2} />
            <Box className="label_2_r" color={baseColors.label_assistive_2}>
              {desc}
            </Box>
          </>
        )}
      </Box>
      <ColorIcon
        name="icon-arrow-right-small-mono"
        color={emphasis ? baseColors.orange_500 : baseColors.label_assistive_2}
        size={16}
      />
    </BaseStack>
  );
}

/**
 * 상황별 바로가기. 장례식장명 검색에는 장례가 난 사람, 비용을 알아보는 사람, 조문 가는 사람이 섞여 있다.
 * 새 페이지를 만들지 않고 이미 있는 고유 페이지로 보낸다. 얇은 의도 페이지를 새로 찍으면
 * 템플릿 중복으로 다시 묶인다. 각 의도 페이지는 "{이름} 주차", "{이름} 견적" 같은 롱테일을 맡는다.
 *
 * - 장례 발생: 바로 고이 대표번호 전화. 긴급이라 새벽 안내 없이 연결(isUrgent)
 * - 비용, 절차: www 견적폼 (canonical 은 이 상세를 가리킨다)
 * - 조문, 발인: parlor 도메인 directions (네이버 하락에서 버틴 표면)
 *
 * 가격 공개 실험과 무관하게 실험군, 대조군 모두 같은 자리에 둔다.
 */
function ParlorIntentShortcuts({ parlor }: { parlor: ParlorDetailProps }) {
  const { openParlorInquiryModal } = useParlorInquiryModal("parlor_detail");

  if (isHiddenParlor(parlor)) return null;

  const id = parlor.id;
  const name = parlor.companyname;

  return (
    <BaseStack width="100%" px={16} py={20} color={baseColors.label_normal}>
      <h2 className="headline_2_sb">{`${name} 이용 안내`}</h2>
      <Spacing gap={4} />
      <Box className="label_2_r" color={baseColors.label_assistive_2}>
        고이장례연구소에서 e하늘 장사정보시스템 기반으로 안내드려요.
      </Box>
      <Spacing gap={12} />
      <BaseStack spacing={8}>
        {/* tel 링크라 JS 없이도 전화가 걸린다. 클릭 시에는 하단 전화 버튼과 같은 문의 유형 모달을 띄운다 (빈소, 이송 문의만 새벽에도 연결) */}
        <a
          href="tel:1666-9784"
          onClick={(e) => {
            e.preventDefault();
            track("intent-call", id);
            void openParlorInquiryModal();
          }}
        >
          <ShortcutRow
            icon="icon-call-mono"
            title="빠른 장례상담, 임종접수가 필요해요"
            desc="24시간 바로 전화 연결, 고이에서 모두 도와드려요"
            emphasis
          />
        </a>
        <NextLink href={`/match/parlor-requests/${id}/`} onClick={() => track("intent-cost", id)}>
          <ShortcutRow
            icon="icon-coin-mono"
            title="장례식장 비용 알아보기"
            desc={`${name} 조문객 수에 맞춘 장례식장 비용 견적 받기`}
          />
        </NextLink>
        <a
          href={`https://parlor.goifuneral.co.kr/directions/${id}/?utm_source=platform&utm_medium=parlor_detail&utm_content=intent_visit`}
          onClick={() => track("intent-visit", id)}
        >
          <ShortcutRow
            icon="icon-pin-location-mono"
            title="조문을 가려고 해요"
            desc="위치, 주차, 대중교통 안내"
          />
        </a>
      </BaseStack>
    </BaseStack>
  );
}

export default ParlorIntentShortcuts;
